import { useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  IconButton,
  CircularProgress,
  Modal,
} from '@mui/material';
import {
  faImage,
  faSend,
  faXmark,
  faLink,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import toast from 'react-hot-toast';
import _ from 'lodash';
import sanitizeHtml from 'sanitize-html';
import classNames from 'classnames';
import { WithRouterProps, browserHistory, withRouter } from 'react-router';
import { useAddCommunityPost } from '../hooks/useAddCommunityPost';
import { ExitModalContent } from './ExitModalContent';
import { maxFileSizeBytes } from '@client/new/common/videos/videos.constants';
import { trackEvent } from '@services/tracking';
import {
  containsCommunityMention,
  getChannelMentionTriggerPosition,
  insertChannelMention,
} from '../utils/channelMention';
import { MDTextarea } from './MDTextarea';
import { MDTextareaHandle } from './MDTextarea';
import { MDActionIcon } from './MDActionIcon';
import { Col } from '@design-system/components/grid/grid';
import { ChannelMentionConfirmation } from './ChannelMentionConfirmation';

const warnBeforeUnload = (ev: BeforeUnloadEvent) => {
  ev.preventDefault();
  ev.returnValue = '';
};

type AddPostProps = {
  communityId: number;
  ownerProfilePicture: string;
  ownerName: string;
  memberCount?: number;
} & WithRouterProps;

const AddPost = (props: AddPostProps) => {
  const {
    communityId,
    ownerName,
    ownerProfilePicture,
    memberCount,
    router,
    routes,
  } = props;
  const [media, setMedia] = useState<File[]>([]);
  const [post, setPost] = useState('');
  const [isAddingPost, setIsAddingPost] = useState(false);
  const [hasError, setHasEror] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [nextPath, setNextPath] = useState<string | undefined>(undefined);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const mdTextareaRef = useRef<MDTextareaHandle | null>(null);
  const [mentionAnchor, setMentionAnchor] = useState<HTMLElement | null>(null);
  const [triggerStart, setTriggerStart] = useState<number>(0);
  const [showChannelConfirmation, setShowChannelConfirmation] = useState(false);

  useEffect(() => {
    router.setRouteLeaveHook(routes[0], (nextLocation) => {
      if ((post.length > 0 || media.length > 0) && !nextPath) {
        nextLocation?.pathname && setNextPath(nextLocation.pathname);
        setModalVisible(true);
        return false;
      }
    });
  }, [media.length, post, routes, nextPath, setNextPath, router]);

  const onAddSuccess = () => {
    trackEvent({
      type: 'Post Created',
      payload: {
        'Media Count': media.length,
        'Media Type': media.map((m) => m.type).join(', '),
      },
    });

    setPost('');
    setMedia([]);
    if (inputRef?.current) {
      inputRef.current.value = '';
    }
    setIsAddingPost(false);
  };

  const onAddError = () => {
    setHasEror(true);
    setIsAddingPost(false);

    trackEvent({
      type: 'Post Failed',
      payload: {
        'Media Count': media.length,
        'Media Type': media.map((m) => m.type).join(', '),
        // Legacy community flow slated for removal; failure reason is not
        // threaded here, only communities-v2 reports it.
        Reason: '',
      },
    });
  };

  const addPostMutation = useAddCommunityPost(
    communityId,
    ownerName,
    ownerProfilePicture,
    onAddSuccess,
    onAddError,
  );

  useEffect(() => {
    post.length > 0 || media.length > 0
      ? window.addEventListener('beforeunload', warnBeforeUnload)
      : window.removeEventListener('beforeunload', warnBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', warnBeforeUnload);
    };
  }, [post, media]);

  const addMedia = (ev: React.ChangeEvent<HTMLInputElement>) => {
    if (ev.target.files && ev.target.files.length + media.length > 5) {
      toast.error('You can upload max. 5 media files');
      return;
    }
    if (ev.target.files && !_.isEmpty(ev.target.files)) {
      const validMedia = Array.from(ev.target.files).filter(
        (file) => file.size <= maxFileSizeBytes,
      );
      if (validMedia.length !== ev.target.files.length) {
        toast.error('You cannot upload files bigger than 100MB');
      }
      setMedia([...media, ...validMedia]);
    }
  };

  const executePost = () => {
    if (post.length > 4000) {
      toast.error("Posts can't be longer than 4K characters.");
      return;
    }
    setIsAddingPost(true);
    const sanitizedPost = sanitizeHtml(post, {
      allowedTags: ['a'],
      disallowedTagsMode: 'escape',
    });
    addPostMutation.mutate({ text: sanitizedPost, media: media });
  };

  const postMessage = () => {
    if (containsCommunityMention(post)) {
      setShowChannelConfirmation(true);
    } else {
      executePost();
    }
  };

  const handleConfirmChannelMention = () => {
    setShowChannelConfirmation(false);
    executePost();
  };

  const handleCancelChannelMention = () => {
    setShowChannelConfirmation(false);
  };

  const handleTextareaKeyUp = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const target = e.target as HTMLTextAreaElement;
    const cursorPos = target.selectionStart || 0;

    const triggerPos = getChannelMentionTriggerPosition(post, cursorPos);

    if (triggerPos >= 0) {
      setTriggerStart(triggerPos);
      setMentionAnchor(target);
    } else {
      setMentionAnchor(null);
    }
  };

  const handleMentionSelect = () => {
    const cursorPos = textAreaRef.current?.selectionStart || post.length;
    const newPost = insertChannelMention(post, cursorPos, triggerStart);
    setPost(newPost);
    setMentionAnchor(null);
  };

  const deleteImage = (fileName: string) => {
    setMedia(media.filter((f) => f.name !== fileName));
    if (inputRef?.current) {
      inputRef.current.value = '';
    }
  };

  const onDismiss = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setModalVisible(false);
    setNextPath(undefined);
  };

  const onExit = () => {
    if (nextPath) {
      setModalVisible(false);
      browserHistory.push(nextPath);
    }
  };
  return (
    <>
      <Modal open={modalVisible} onClose={onDismiss}>
        <>
          <ExitModalContent onDismiss={onDismiss} onExit={onExit} />
        </>
      </Modal>
      <Box className="flex flex-col gap-10 p-5">
        <Box className="flex gap-2.5 items-center">
          <Avatar
            alt={'profilePicture'}
            src={ownerProfilePicture}
            data-testid="avatar"
            className="self-start"
          />
          <Col>
            <MDTextarea ref={mdTextareaRef} value={post} onChange={setPost} />
          </Col>
        </Box>
        {!_.isEmpty(media) && (
          <div className="flex flex-row gap-3 flex-wrap">
            {media.map((file) => (
              <div key={file.name} className="relative w-[fit-content]">
                <IconButton
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: 10,
                    position: 'absolute',
                    top: -10,
                    right: -10,
                    backgroundColor: 'white',
                    boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.15)',
                  }}
                  size="large"
                  onClick={() => deleteImage(file.name)}
                >
                  <FontAwesomeIcon
                    icon={faXmark}
                    className="text-xs text-gray-1"
                  />
                </IconButton>
                {file.type.startsWith('image') && (
                  <img
                    src={URL.createObjectURL(file)}
                    className="max-h-[138px]"
                  />
                )}
                {file.type.startsWith('video') && (
                  <video
                    src={URL.createObjectURL(file)}
                    className="max-h-[138px]"
                  />
                )}
              </div>
            ))}
          </div>
        )}
        <Box className="flex justify-between items-center">
          <div className="flex gap-2">
            <MDActionIcon
              icon={faImage}
              ariaLabel="Add media"
              onClick={() => inputRef.current?.click()}
            />
            <MDActionIcon
              icon={faLink}
              ariaLabel="Add link"
              onClick={() => mdTextareaRef.current?.openAddLink()}
            />
            <input
              type="file"
              accept="image/jpeg, image/png, video/mp4, video/quicktime"
              ref={inputRef}
              onChange={addMedia}
              hidden
              multiple
            />
          </div>
          <Button
            variant="contained"
            className={classNames('bg-purple-1 disabled:bg-primary-150', {
              'bg-purple-1': !hasError,
              'bg-orange-1': hasError,
            })}
            startIcon={<FontAwesomeIcon icon={faSend} className="text-xs" />}
            onClick={postMessage}
            disabled={(post.length === 0 && media.length === 0) || isAddingPost}
          >
            {isAddingPost ? (
              <CircularProgress size={20} />
            ) : hasError ? (
              'Retry'
            ) : (
              'Post'
            )}
          </Button>
        </Box>
      </Box>

      <ChannelMentionConfirmation
        open={showChannelConfirmation}
        memberCount={memberCount}
        onConfirm={handleConfirmChannelMention}
        onCancel={handleCancelChannelMention}
      />
    </>
  );
};

export default withRouter(AddPost);
